import { fromUnixTime, differenceInMilliseconds } from 'date-fns';
import { EventTimeline, EventType, KnownMembership, MatrixClient, type Room } from 'matrix-js-sdk/src/matrix';
import moment from 'moment';

import { getIsAllowedEventType } from 'modules/MeetingChat/utils';
import constants from 'utils/Constants';
import {
  CustomMeedioMatrixEvents,
  isSystemUser,
  isValidRoomScheduledStateEvent,
  isValidRoomScheduledStateEventWithEndDate,
  isValidRoomTopicStateEvent,
} from 'utils/matrixUtils';
import { ScheduledDate } from 'utils/types';

export const getRoomCreationTimestamp = (room: Room): number => {
  const roomState = room.getLiveTimeline().getState(EventTimeline.FORWARDS);
  const createdEvent = roomState?.getStateEvents(EventType.RoomCreate)?.[0];
  return createdEvent?.getTs() ?? 0;
};

/**
 * Returns the timestamp of the current user's join event for the room, or 0 if the
 * user is not joined or the event is unavailable. Used as a recency floor so that
 * an accepted invite sorts to the top of the conversation list regardless of
 * pre-join timeline activity from the inviter.
 */
export const getMyJoinTimestamp = (room: Room, userId: string): number => {
  const member = room.getMember(userId);
  if (!member || member.membership !== KnownMembership.Join) return 0;
  const memberEvent = member.events?.member;
  return memberEvent?.getTs() ?? memberEvent?.localTimestamp ?? 0;
};

/**
 * Last allowed “activity” event on the timeline, or room creation time if the room
 * has no messages yet. When `userId` is provided the result is floored to the user's
 * own join timestamp so that a freshly accepted invite always sorts above rooms whose
 * last activity pre-dates the accept.
 */
export const getRoomSortRecencyTimestamp = (room: Room, userId?: string | null): number => {
  const timeline = room.timeline ?? [];
  let activityTs = 0;
  for (let i = timeline.length - 1; i >= 0; i--) {
    const event = timeline[i];
    if (getIsAllowedEventType(event.getType())) {
      activityTs = event.localTimestamp ?? event.getTs() ?? 0;
      break;
    }
  }

  if (activityTs === 0) activityTs = getRoomCreationTimestamp(room);

  if (!userId) return activityTs;

  return Math.max(activityTs, getMyJoinTimestamp(room, userId));
};

const compareByDates = (firstRoom: Room, secondRoom: Room, userId?: string | null) => {
  const firstRoomTime = timestampToDate(getRoomSortRecencyTimestamp(firstRoom, userId));
  const secondRoomTime = timestampToDate(getRoomSortRecencyTimestamp(secondRoom, userId));

  return differenceInMilliseconds(secondRoomTime, firstRoomTime);
};

export const sortRooms = (rooms?: Room[], userId?: string | null) =>
  rooms ? rooms.slice().sort((a, b) => compareByDates(a, b, userId)) : [];
export const timestampToDate = (timestamp: number) => fromUnixTime(timestamp / 1000);

export const getRoomPathIdentifier = (room: Room) => {
  const canonicalAlias = room.getCanonicalAlias();
  if (canonicalAlias) return canonicalAlias.slice(1).split(':')[0];

  return room.roomId;
};

export const getRoomMetadata = (room: Room) => {
  let description: string | null = null;
  let scheduledToStartAt: ScheduledDate = null;
  let scheduledToEndAt: ScheduledDate = null;
  let createdAt: number | null = null;

  const roomState = room.getLiveTimeline().getState(EventTimeline.FORWARDS);
  const scheduledContent = roomState
    ?.getStateEvents(CustomMeedioMatrixEvents.IS_SCHEDULED_CALL_STATE_EVENT)?.[0]
    ?.getContent();

  if (isValidRoomScheduledStateEvent(scheduledContent)) scheduledToStartAt = scheduledContent.startsAt;
  if (isValidRoomScheduledStateEventWithEndDate(scheduledContent)) scheduledToEndAt = scheduledContent.endsAt;

  const createEvent = roomState?.getStateEvents(EventType.RoomCreate)?.[0];
  if (createEvent) createdAt = createEvent.getTs();

  const descriptionContent = roomState?.getStateEvents(EventType.RoomTopic)?.[0]?.getContent();
  if (isValidRoomTopicStateEvent(descriptionContent)) description = descriptionContent.topic;

  return { createdAt, description, scheduledToStartAt, scheduledToEndAt };
};

type SessionDetails = { isActive: boolean; participantsCount: number; endedAt: number | null };
const initialSessionDetails = { isActive: false, participantsCount: 0, endedAt: null };

type CallMemberEvent = {
  getContent: () => Record<string, unknown>;
  getTs: () => number;
  getSender: () => string | undefined;
};

const getIsRoomActiveForCall = (matrixClient: MatrixClient, room: Room): boolean => {
  const { scheduledToStartAt, scheduledToEndAt } = getRoomMetadata(room);
  const isScheduledRoomActiveTime =
    !!scheduledToStartAt &&
    moment().isSameOrAfter(scheduledToStartAt) &&
    (!scheduledToEndAt || moment().isBefore(scheduledToEndAt));

  // State events might be missing a 'leave' event, RTC session seem to be more reliable for detecting activity
  const isRtcSessionActive = !!matrixClient.matrixRTC.getActiveRoomSession(room);
  // Scheduled room is marked as active between the start and end time, even if there's no participants in meeting
  return isRtcSessionActive || isScheduledRoomActiveTime;
};

const getCallMemberLiveFlags = (event: CallMemberEvent, room: Room) => {
  const content = event.getContent();
  const contentExists = !!Object.keys(content).length;
  const senderMembership = room.getMember(event.getSender() || '')?.membership;
  const isParticipantBanned = senderMembership === KnownMembership.Ban;
  const isParticipantJoined = senderMembership === KnownMembership.Join;
  // Staleness guard (Bug 2): per MSC3401 there is no server-side TTL for
  // m.call.member state. A non-empty event whose session start (created_ts,
  // stable across re-announcements; falls back to getTs()) is older than
  // MAX_INSTANT_CALL_MS (8h) is treated as a dead/stuck session, not a live one —
  // the same threshold the Messenger uses for its instant-call guard.
  const sessionStartTs = (content.created_ts as number | undefined) ?? event.getTs();
  const isStale = Date.now() - sessionStartTs >= constants.MAX_INSTANT_CALL_MS;

  return { contentExists, isParticipantBanned, isParticipantJoined, isStale };
};

const isLiveCallMemberEvent = (event: CallMemberEvent, room: Room, isRoomActive: boolean): boolean => {
  const { contentExists, isParticipantBanned, isParticipantJoined, isStale } = getCallMemberLiveFlags(event, room);
  return contentExists && isRoomActive && isParticipantJoined && !isParticipantBanned && !isStale;
};

/** Live RTC participant mxids from `m.call.member` state — same filter as `getCallSessionDetails`. */
export const getActiveCallParticipantMxids = (matrixClient: MatrixClient, room: Room): string[] => {
  const callEvents = room
    .getLiveTimeline()
    .getState(EventTimeline.FORWARDS)
    ?.getStateEvents(EventType.GroupCallMemberPrefix);

  if (!callEvents?.length) return [];

  const isRoomActive = getIsRoomActiveForCall(matrixClient, room);
  const mxids: string[] = [];
  const seen = new Set<string>();

  for (const event of callEvents) {
    if (!isLiveCallMemberEvent(event, room, isRoomActive)) continue;
    const sender = event.getSender();
    if (!sender || isSystemUser(sender) || seen.has(sender)) continue;
    seen.add(sender);
    mxids.push(sender);
  }

  return mxids;
};

export const getCallSessionDetails = (matrixClient: MatrixClient, room: Room) => {
  const isRoomActive = getIsRoomActiveForCall(matrixClient, room);

  // callEvent === a matrix user in the room
  // a matrix user should have a single event that gets updated based on whether they are in the meeting or not
  const callEvents = room
    .getLiveTimeline()
    .getState(EventTimeline.FORWARDS)
    ?.getStateEvents(EventType.GroupCallMemberPrefix);

  // No one joined this call yet
  if (!callEvents?.length) return initialSessionDetails;

  const sessionDetails = callEvents.reduce<SessionDetails>((acc, event) => {
    const { contentExists, isParticipantBanned, isParticipantJoined, isStale } = getCallMemberLiveFlags(event, room);
    const isLiveParticipant = contentExists && isParticipantJoined && !isParticipantBanned && !isStale;
    const participantsCount = acc.participantsCount + (isLiveParticipant ? 1 : 0);

    if (contentExists && isRoomActive && isParticipantJoined && !isParticipantBanned && !isStale) {
      return { participantsCount, isActive: true, endedAt: null };
    }

    const { endedAt } = acc;
    const eventTimestamp = event.getTs();
    const latestTimestamp = endedAt && endedAt > eventTimestamp ? endedAt : eventTimestamp;
    const newEndedAt = acc.isActive ? null : latestTimestamp;

    return { participantsCount, isActive: acc.isActive, endedAt: newEndedAt };
  }, initialSessionDetails);

  return sessionDetails;
};
